import {
    Component, EventEmitter, Input, OnDestroy, OnInit, Output, TemplateRef, ViewChild, ViewContainerRef, ViewEncapsulation
} from '@angular/core';
import {TemplatePortal} from '@angular/cdk/portal';
import {Filter} from "../../api/filter-interface";
import {FilterGroup} from "../../basic-entity-back/basic-entity-interface/mapping-external";
import {BooleanFilter} from "../../basic-entity-back/filters/boolean-filter";
import {
    AfterDateFilter,
    BeforeDateFilter,
    BetweenDateFilter,
    DateFilters,
    StrictlyAfterDateFilter,
    StrictlyBeforeDateFilter
} from "../../basic-entity-back/filters/date-filter";
import {ExistsFilter} from "../../basic-entity-back/filters/exists-filter";
import {NumericFilter} from "../../basic-entity-back/filters/numeric-filter";
import {
    BetweenRangeFilter,
    GreaterThanOrEqRangeFilter,
    GreaterThanRangeFilter,
    LowerThanOrEqRangeFilter,
    LowerThanRangeFilter,
    RangeFilters
} from "../../basic-entity-back/filters/range-filters";
import {
    EndSearchFilter,
    ExactSearchFilter,
    MultipleSearchFilter,
    PartialSearchFilter,
    SimpleSearchFilter,
    StartSearchFilter,
    WordStartSearchFilter
} from "../../basic-entity-back/filters/search-filter";
import {InternalPropertyMap} from "../../basic-entity-back/basic-entity-interface/mapping-internal";
import {EntityDatasource} from "../../api/entity.datasource";
import {FormGroup, UntypedFormBuilder} from "@angular/forms";
import {ActivatedRoute, Params, Router} from "@angular/router";
import {SearchService} from "../../services/search.service";
import {RouteFilterTranslator} from "./route-filter-translator";
import {Subscription} from "rxjs";
import {FilterAndData} from "../../api/filter-list";
import {GeneralSearchFilter} from "../../basic-entity-back/filters/general-search-filter";
import {BasicEntityTableConstants} from "../basic-entity-table/basic-entity-table-constants";
import {TypeStr} from "../../basic-entity-back/property-type/type-str";
import {InterfaceProviderService} from "../../basic-entity-back/services/interface-provider.service";
import {FilterListComponent} from "./filter-list/filter-list.component";
import {filtrosAlAbrir, filtrosConBusqueda, hayBotonPorDefecto, hayFiltrosDelPanel, paramsDeFiltros} from "./filtros-botones";
import {filtrosFueraDelPanel, sinFiltrosFueraDelPanel} from "./filtros-fuera-del-panel";
import {
    filtrosAlCambiarPanel, filtrosAplicados, filtrosDeBotonYFijos, filtrosPorDefecto, SeccionFiltro, valoresDesdeFiltros
} from "./panel-filtros/panel-filtros";


interface FilterRepresentation {
    name: string;
    icon?: string;
    description: string;
}


@Component({
    selector: 'be-filtering2',
    templateUrl: './basic-entity-filtering2.component.html',
    styleUrls: ['./basic-entity-filtering2.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class BasicEntityFiltering2Component<T> implements OnInit, OnDestroy {
    /** Time that the user has to stay without typing before the search is performed */
    private static readonly TIMEOUT_BEFORE_SEARCHING = 500;
    private static readonly FILTER_REPRESENTATION = new Map<Filter | FilterGroup, FilterRepresentation>([
        [BooleanFilter, {
            name: 'Exacto', icon: 'fa-toggle-on',
            description: 'Busca elementos con un valor en esta propiedad de "verdadero" o "falso".'
        }],
        [AfterDateFilter, {
            name: 'Después', icon: 'fa-calendar',
            description: 'Busca elementos cuyo valor en esta propiedad es posterior o igual a cierta fecha.'
        }],
        [BeforeDateFilter, {
            name: 'Antes', icon: 'fa-calendar',
            description: 'Busca elementos cuyo valor en esta propiedad es previo o igual a cierta fecha.'
        }],
        [StrictlyAfterDateFilter, {
            name: 'Estrictamente después', icon: 'fa-calendar',
            description: 'Busca elementos cuyo valor en esta propiedad es estrictamente posterior a cierta fecha.'
        }],
        [StrictlyBeforeDateFilter, {
            name: 'Estrictamente antes', icon: 'fa-calendar',
            description: 'Busca elementos cuyo valor en esta propiedad es estrictamente previo a cierta fecha.'
        }],
        [BetweenDateFilter, {
            name: 'Entre', icon: 'fa-calendar',
            description: 'Busca elementos cuyo valor en esta propiedad está entre dos fechas.'
        }],
        [ExistsFilter, {
            name: '¿Nulo?', icon: 'fa-times',
            description: 'Busca elementos cuyo valor en esta propiedad es (o no es) nulo.'
        }],
        [NumericFilter, {
            name: 'Exacto', icon: 'fa-equals',
            description: 'Busca elementos cuyo valor en esta propiedad es exactamente el número indicado.'
        }],
        [GreaterThanRangeFilter, {
            name: 'Mayor', icon: 'fa-greater-than',
            description: 'Busca elementos cuyo valor en esta propiedad es mayor que cierto valor.'
        }],
        [GreaterThanOrEqRangeFilter, {
            name: 'Mayor o igual', icon: 'fa-greater-than-equal',
            description: 'Busca elementos cuyo valor en esta propiedad es mayor o igual que cierto valor.'
        }],
        [LowerThanRangeFilter, {
            name: 'Menor', icon: 'fa-less-than',
            description: 'Busca elementos cuyo valor en esta propiedad es menor que cierto valor.'
        }],
        [LowerThanOrEqRangeFilter, {
            name: 'Menor o igual', icon: 'fa-less-than-equal',
            description: 'Busca elementos cuyo valor en esta propiedad es menor o igual que cierto valor.'
        }],
        [BetweenRangeFilter, {
            name: 'Entre', icon: 'fa-band-aid',
            description: 'Busca elementos cuyo valor en esta propiedad se sitúa entre dos valores.'
        }],
        [MultipleSearchFilter, {
            name: 'Filtrar', icon: 'fa-search',
            description: 'Busca elementos cuyo valor en esta propiedad contiene un valor dado.'
        }],
        [SimpleSearchFilter, {
            name: 'Filtrar', icon: 'fa-search',
            description: 'Busca elementos cuyo valor en esta propiedad contiene un valor dado.'
        }],
        [StartSearchFilter, {
            name: 'Filtrar', icon: 'fa-search',
            description: 'Busca elementos cuyo valor en esta propiedad comienza con un valor dado.'
        }],
        [WordStartSearchFilter, {
            name: 'Filtrar', icon: 'fa-search',
            description: 'Busca elementos cuyo valor en esta propiedad tiene alguna palabra que comience por los carácteres dados.'
        }],
        [ExactSearchFilter, {
            name: 'Filtrar', icon: 'fa-search',
            description: 'Busca elementos cuyo valor en esta propiedad es exactamente alguna de las palabras dadas (separando por espacios, si cabe).'
        }],
        [EndSearchFilter, {
            name: 'Filtrar', icon: 'fa-search',
            description: 'Busca elementos cuyo valor en esta propiedad termina con un valor dado.'
        }],
        [PartialSearchFilter, {
            name: 'Filtrar', icon: 'fa-search',
            description: 'Busca elementos cuyo valor en esta propiedad contiene un valor determinado.'
        }],
        [DateFilters, {
            name: 'Filtrar', icon: 'fa-calendar',
            description: 'Busca elementos cuya fecha es anterior, posterior o igual a la fecha dada.'
        }],
        [RangeFilters, {
            name: 'Rango', icon: 'fa-band-aid',
            description: 'Busca elementos cuyo valor es mayor que cierto valor, menor que cierto valor o se sitúa entre dos valores.'
        }]
    ]);


    /** Properties to offer filters for */
    @Input() public properties: InternalPropertyMap[];
    /** Properties which can be filtered (obtained from the input properties) */
    public filtrableProperties: InternalPropertyMap[];
    /** The datasource this component should manage to make the filtering */
    @Input() protected dataSource: EntityDatasource<T>;
    @Input() public fixedFilteringProperties: InternalPropertyMap[] = [];
    /** Botones de filtro de la tabla, si los hay: su filtro no enciende el embudo */
    @Input() public customFilteringButtons: any[] = [];
    /** Con panel, el embudo abre el panel de filtros (casillas y textos, se aplica al momento) en vez de la lista */
    @Input() public panelFiltros: SeccionFiltro[] | null = null;
    /** Se abre el panel (el embudo): la tabla carga entonces las opciones de las relaciones, no al abrir la página */
    @Output() public panelAbierto = new EventEmitter<void>();
    /** Los filtros que trae la URL */
    public filtrosUrl: FilterAndData[] = [];
    /**
     * Los de la URL que son del panel, los que pinta: sin el botón, los fijos, la búsqueda general ni los que no son de
     * ninguna sección (esos se conservan aparte en cada navegación del panel, ver `aplicarPanel`)
     */
    public filtrosPanel: FilterAndData[] = [];
    /** Cuántos filtros del panel hay puestos: el número que va en el embudo */
    public contadorPanel = 0;
    /** Timeout to reload the filters (while you type this timeout is renovated) */
    private _filtersReloadTimeout = null;
    /** Subscription to the general search in the search bar */
    private _searchSubscription: Subscription = null;
    private _queryParamsSubscription: Subscription = null;
    /**
     * Con panel y botones, los botones atienden la búsqueda general y cargan la tabla en cada cambio de la URL:
     * si este componente cargara también, cada cambio pediría la colección dos veces
     */
    private _buscanLosBotones = false;
    formGroupFiltering: FormGroup;
    dateOperator: any;
    protected readonly TypeStr = TypeStr;
    @ViewChild(FilterListComponent, {static: true}) filterList: FilterListComponent;
    @ViewChild('embudo', {static: true}) private _embudoTpl: TemplateRef<unknown>;
    private _embudo: TemplatePortal;
    withFilters: any;

    constructor(private _router: Router,
                private _activatedRoute: ActivatedRoute,
                private _searchService: SearchService,
                private _routeFilterTranslator: RouteFilterTranslator,
                private _interfaceProvider: InterfaceProviderService,
                private _fb: UntypedFormBuilder,
                private _viewContainerRef: ViewContainerRef) {
    }

    ngOnInit(): void {
        this.filtrableProperties = [];
        const filtrablePropertiesTmp = this.properties.sort((a, b) => {
            if (a.order < b.order) {
                return -1;
            } else if (a.order > b.order) {
                return 1;
            } else {
                return 0;
            }
        }).filter(prop => prop.filters.length > 0 && !prop.fueraDelPanelDeFiltros);
        this.fixedFilteringProperties.forEach(fp => {
            if (filtrablePropertiesTmp.findIndex(fpt => fp.key === fpt.key) > -1) {
                this.filtrableProperties.push(fp);
            }
        })
        filtrablePropertiesTmp.forEach(fp => {
            if (this.fixedFilteringProperties.findIndex(fpt => fp.key === fpt.key) === -1) {
                this.filtrableProperties.push(fp);
            }
        })


        // Con panel la lista clásica va oculta y nadie la lee: llenarla crearía sus be-input (uno, el del lote,
        // carga entera la colección de lotes en cada visita) para nada
        if (!this.panelFiltros) {
            this.filtrableProperties.forEach(fp => {
                fp.filters.forEach(f => this.filterList.addFilter(fp, f))
            })
        }

        this._searchService.hideSearch = false;
        this._decidirModo();
        this._searchSubscription = this._buscanLosBotones ? null : this._searchService.search.subscribe(
            newSearch => this._redirectGeneralSearch(newSearch)
        );
        this._queryParamsSubscription = this._activatedRoute
            .queryParams.subscribe(this._manageParamsChange.bind(this));

        // Diferido: la cabecera ya se ha revisado en esta vuelta de detección de cambios y
        // cambiarle el embudo ahora daría ExpressionChangedAfterItHasBeenChecked
        this._embudo = new TemplatePortal(this._embudoTpl, this._viewContainerRef);
        const embudo = this._embudo;
        Promise.resolve().then(() => {
            if (this._embudo === embudo) {  // la tabla puede haberse destruido entretanto
                this._searchService.ponerEmbudo(embudo);
            }
        });
    }


    /**
     * Con panel y botones, la búsqueda general la atienden los botones (conservando el panel): si la atendieran los
     * dos, cada uno navegaría por su lado y ganaría el último. Los botones (Piezas y Pedidos los meten en el
     * constructor) y el panel (el explícito de Piezas o el que genera be-table en su ngOnInit, que va antes que el
     * de este componente) ya están aquí.
     */
    private _decidirModo(): void {
        this._buscanLosBotones = !!this.panelFiltros && this.customFilteringButtons.length > 0;
    }

    private _manageParamsChange(params: Params) {
        const filters = {};
        Object.entries(this._routeFilterTranslator.paramsToFilters(params, this.properties))
            .filter(([k, f]) => f.data != null)
            .forEach(([k, f]) => filters[k] = f);

        if (this.panelFiltros) {
            // Sin nada en la URL, la tabla se abre con los valores por defecto del panel. Se sustituye la entrada
            // del historial (si no, Atrás volvería a la URL vacía, que rebotaría aquí) y no se carga: carga la
            // emisión siguiente, ya con ellos. Tras quitarlo todo, la URL lleva `cleared` y no vuelven. Con un botón
            // por defecto (Pedidos) no se aplican: manda el botón, que pone be-filtering-buttons.
            const porDefecto = hayBotonPorDefecto(this.customFilteringButtons)
                ? [] : filtrosPorDefecto(this.panelFiltros, this.properties);
            const iniciales = filtrosAlAbrir(Object.values(filters), !!params['cleared'], porDefecto);
            if (iniciales) {
                this._navegarConPanel(iniciales, true);
                return;
            }
        }

        this.dataSource.filtros = Object.values(filters);
        // El embudo se calcula con lo que trae la URL: al volver a la tabla, o al recargar, el
        // componente es nuevo y no ha aplicado él los filtros
        this.filtrosUrl = this.dataSource.filtros;
        if (this.panelFiltros) {
            // Al panel solo le llega lo suyo, y el embudo cuenta solo eso: ni el botón, ni la búsqueda general, ni los
            // fijos, ni lo de fuera del panel (el between de un botón se leería como el desde / hasta de la sección de
            // fechas de esa misma propiedad)
            this.filtrosPanel = filtrosDeBotonYFijos(this.filtrosUrl, this.customFilteringButtons,
                this.fixedFilteringProperties, this.panelFiltros).panel;
            this.contadorPanel = filtrosAplicados(this.panelFiltros, valoresDesdeFiltros(this.panelFiltros, this.filtrosPanel)).length;
            this.withFilters = this.contadorPanel > 0;
        } else {
            this.withFilters = hayFiltrosDelPanel(sinFiltrosFueraDelPanel(this.dataSource.filtros), this.customFilteringButtons);
        }
        this.filterList.filters.forEach(fp => {
            const res = this.dataSource.filtros.find(f2 => f2.property === fp.property);
            if (res !== undefined) {
                fp.control.setValue(res, {emitEvent: false});
            }
        });

        const generalSearch = this.dataSource.filtros.find(f => f.filter === GeneralSearchFilter);
        if (generalSearch) {
            this._searchService.currentSearch = generalSearch.data;
        } else {
            this._searchService.currentSearch = null;
        }
        // La carga la hacen los botones con esta misma emisión (ver _buscanLosBotones)
        if (this._buscanLosBotones) {
            return;
        }
        clearTimeout(this._filtersReloadTimeout);
        this._filtersReloadTimeout = setTimeout(
            () => this.dataSource.load(),
            BasicEntityFiltering2Component.TIMEOUT_BEFORE_SEARCHING);
    }


    /**
     * @inheritDoc
     */
    ngOnDestroy(): void {
        this._searchSubscription?.unsubscribe();
        this._queryParamsSubscription.unsubscribe();
        this._searchService.quitarEmbudo(this._embudo);
        this._embudo = null;
        // this._searchService.hideSearch = true;
    }

    /**
     * Performs the navigation to set the filter for the general search.
     * If str is null it does nothing.
     * @private
     */
    private _redirectGeneralSearch(str: string | null) {
        if (str == null) {
            return;
        }
        if (this.panelFiltros) {
            this._navegarConPanel(filtrosConBusqueda(this.filtrosUrl, str));
            return;
        }

        let filterList: { [id: string]: FilterAndData } = {};
        for (const filter of this.filterList.filters) {
            filterList[filter.id] = filter.control.value;
        }

        if (str !== '') {
            filterList['gs'] = new FilterAndData(GeneralSearchFilter, str);
        }

        this._filterNavigation(filterList);
    }

    private _filterNavigation(list: { [id: string]: FilterAndData }) {
        const filtered = {};
        Object.entries(list)
            .filter(([id, f]) => f && f.data != null)
            .forEach(([id, f]) => filtered[id] = f);

        // Los que no están en el panel se mantienen: la URL se rehace solo con lo del panel
        const params = RouteFilterTranslator.sFiltersToParams({...filtered, ...filtrosFueraDelPanel(this.dataSource.filtros)});

        // Preserve pagination
        if (this.dataSource.pagination.pageSize) {
            params[BasicEntityTableConstants.PAGINATION_SIZE] = this.dataSource.pagination.pageSize;
        }

        this.withFilters = Object.values(filtered).length > 0;

        this._router.navigate([], {
            queryParams: params,
            queryParamsHandling: ""
        }).catch(reason =>
            console.error("Navigation rejected", reason)
        );
    }

    clearFilters() {
        let filterListFixed: { [id: string]: FilterAndData } = {};
        for (const filter of this.filterList.filters) {
            if (this.fixedFilteringProperties.findIndex(p => p.key === filter.property.key) > -1) {
                filterListFixed[filter.id] = filter.control.value;
            }
        }

        this.filterList.setFilters(filterListFixed);
        this.filtrableProperties.forEach(fp => {
            if (this.fixedFilteringProperties.findIndex(p => p.key === fp.key) === -1) {
                this.filterList.addFilter(fp, fp.filters[0])
            }
        })
        this._filterNavigation(filterListFixed);
    }

    applyFilters() {
        let filterList: { [id: string]: FilterAndData } = {};
        for (const filter of this.filterList.filters) {
            if (filter.control.value?.data && filter.control.value?.data !== '') {
                filterList[filter.id] = filter.control.value;
            }
        }
        this._filterNavigation(filterList);
    }

    /**
     * El panel ha cambiado: va a la URL con lo nuevo del panel y todo lo que no es suyo, que se conserva: el botón
     * pulsado, la búsqueda general, los fijos (llegan por el enlace del menú; «Borrar todos» no los quita), los que
     * pone la pantalla fuera del panel (`seguimientoAnulado` en Enviados) y los que ninguna sección reclama
     */
    aplicarPanel(delPanel: FilterAndData[]) {
        this._navegarConPanel(filtrosAlCambiarPanel(this.filtrosUrl, this.customFilteringButtons,
            this.fixedFilteringProperties, this.panelFiltros, delPanel));
    }

    /** @param reemplazar sustituye la entrada del historial en vez de añadir otra (al ir a los valores por defecto) */
    private _navegarConPanel(filtros: FilterAndData[], reemplazar = false) {
        const params = paramsDeFiltros(filtros);
        if (this.dataSource.pagination.pageSize) {
            params[BasicEntityTableConstants.PAGINATION_SIZE] = this.dataSource.pagination.pageSize;
        }
        // Tras TODOS la URL lleva `cleared` para que no vuelva el botón por defecto: se conserva. Y una URL que
        // el usuario deja sin filtros también lo lleva, para que no vuelvan los valores por defecto del panel
        if (filtros.length === 0 || this._activatedRoute.snapshot.queryParams['cleared']) {
            params['cleared'] = true;
        }
        this._router.navigate([], {queryParams: params, queryParamsHandling: "", replaceUrl: reemplazar})
            .catch(reason => console.error("Navigation rejected", reason));
    }
}
